import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { settingsCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Input } from '@/ui/Field/Field'
import { Link } from '@/ui/Link/Link'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { TabBar, TabPanel } from '@/ui/TabBar/TabBar'
import { workspacesApi } from '../api/workspaces'
import { UsagePanel } from '../components/UsagePanel'
import { WsPageShell } from '../components/WsPageShell'
import { ROUTES } from '../constants/routes'
import { useMeQuery } from '../hooks/queries'
import { useMutationToast } from '../hooks/useMutationToast'
import { useTabParam } from '../hooks/useTabParam'
import { useToastStore } from '../store/toast'
import { useWorkspaceStore } from '../store/workspace'
import type { MeResponse, WorkspaceInfo } from '../types/api'
import styles from './WorkspaceSettingsPage.module.css'
import { AgentsTab } from './workspace-settings/AgentsTab'
import { KeysTab } from './workspace-settings/KeysTab'
import { MembersTab } from './workspace-settings/MembersTab'
import { ProvidersTab } from './workspace-settings/ProvidersTab'

// Workspace administration — members, agents, keys, usage, and providers.
type WorkspaceSubTab = 'members' | 'agents' | 'keys' | 'usage' | 'providers'

const SUB_TABS: readonly WorkspaceSubTab[] = [
  'members',
  'agents',
  'keys',
  'usage',
  'providers',
]

export function WorkspaceSettingsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const addToast = useToastStore((state) => state.addToast)
  const { workspaceId = '' } = useParams<{ workspaceId: string }>()
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId)
  const setActiveWorkspace = useWorkspaceStore((s) => s.setActiveWorkspace)
  // The active sub-tab lives in ?tab= so deep links (ROUTES.workspaceSettingsTab)
  // work and the URL never desyncs from the visible tab.
  const [subTab, setSubTab] = useTabParam(SUB_TABS, 'members')
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleteState, setDeleteState] = useState<
    'idle' | 'checking' | 'pending'
  >('idle')
  const deleteRetryTimer = useRef<
    ReturnType<typeof globalThis.setTimeout> | undefined
  >(undefined)
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [creatingProvider, setCreatingProvider] = useState(false)

  const meQuery = useMeQuery()
  const me = meQuery.data
  const showSkeleton = useSkeletonDelay(meQuery.isPending)

  const workspaces = me?.workspaces ?? []
  const selectedWs = workspaces.find((ws) => ws.workspace_id === workspaceId)
  const canManage = selectedWs?.role === 'owner' || selectedWs?.role === 'admin'
  const isUserManaged = (selectedWs?.management_kind ?? 'user') === 'user'
  const workspaceActive =
    (selectedWs?.lifecycle_status ?? 'active') === 'active'
  const isOwner = selectedWs?.role === 'owner' && isUserManaged
  const deletionPending =
    deleteState === 'pending' || selectedWs?.lifecycle_status === 'deleting'

  // Keep the sidebar switcher in sync with the workspace being edited.
  useEffect(() => {
    if (selectedWs && workspaceId !== activeWorkspaceId) {
      setActiveWorkspace(workspaceId)
    }
  }, [selectedWs, workspaceId, activeWorkspaceId, setActiveWorkspace])

  useEffect(
    () => () => {
      if (deleteRetryTimer.current) {
        globalThis.clearTimeout(deleteRetryTimer.current)
      }
    },
    [],
  )

  function markWorkspaceDeleting(wsId: string) {
    queryClient.setQueryData<MeResponse>(['me'], (current) =>
      current
        ? {
            ...current,
            workspaces: current.workspaces.map((workspace) =>
              workspace.workspace_id === wsId
                ? { ...workspace, lifecycle_status: 'deleting' }
                : workspace,
            ),
          }
        : current,
    )
    queryClient.setQueryData<WorkspaceInfo[]>(['workspaces'], (current = []) =>
      current.map((workspace) =>
        workspace.workspace_id === wsId
          ? { ...workspace, lifecycle_status: 'deleting' }
          : workspace,
      ),
    )
  }

  async function runDelete(wsId: string, wasPending = false) {
    if (deleteRetryTimer.current) {
      globalThis.clearTimeout(deleteRetryTimer.current)
      deleteRetryTimer.current = undefined
    }
    setDeleteState('checking')
    try {
      const response = await workspacesApi.deleteWithMeta(wsId)
      setConfirmingDelete(false)
      if (response.status === 202) {
        markWorkspaceDeleting(wsId)
        setDeleteState('pending')
        if (!wasPending) {
          addToast(settingsCopy.workspaceDelete.committed, 'info')
        }
        const delay = Math.max(250, (response.retryAfterSeconds ?? 1) * 1_000)
        deleteRetryTimer.current = globalThis.setTimeout(() => {
          deleteRetryTimer.current = undefined
          void runDelete(wsId, true)
        }, delay)
        return
      }

      setDeleteState('idle')
      addToast(settingsCopy.workspaceDelete.deleted, 'success')
      void queryClient.invalidateQueries({ queryKey: ['me'] })
      void queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      const remaining = workspaces.filter((w) => w.workspace_id !== wsId)
      if (activeWorkspaceId === wsId) {
        setActiveWorkspace(remaining[0]?.workspace_id ?? null)
      }
      void navigate(ROUTES.home)
    } catch (error) {
      setDeleteState(wasPending ? 'pending' : 'idle')
      addToast(
        settingsCopy.workspaceDelete.failure(
          error instanceof ApiError ? error.status : undefined,
        ),
        'error',
      )
    }
  }

  const renameMutation = useMutationToast({
    mutationFn: (name: string) => workspacesApi.rename(workspaceId, name),
    errorMessage: settingsCopy.page.renameFailed,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['me'] })
      void queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      setRenaming(false)
    },
  })

  function startRename() {
    if (!selectedWs) return
    setRenameValue(selectedWs.name)
    setRenaming(true)
  }

  function commitRename() {
    const trimmed = renameValue.trim()
    if (!selectedWs || !trimmed || trimmed === selectedWs.name) {
      setRenaming(false)
      return
    }
    renameMutation.mutate(trimmed)
  }

  let actions: ReactNode
  if (selectedWs) {
    if (renaming) {
      actions = (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            commitRename()
          }}
        >
          <Input
            autoFocus
            aria-label={settingsCopy.page.nameLabel}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => e.key === 'Escape' && setRenaming(false)}
            className={styles.rename}
          />
        </form>
      )
    } else {
      actions = canManage && workspaceActive && (
        <Button variant="outline" onClick={startRename}>
          {settingsCopy.page.rename}
        </Button>
      )
    }
  }

  let body: ReactNode
  if (meQuery.isPending) {
    body = <div aria-busy="true">{showSkeleton && <Skeleton lines={3} />}</div>
  } else if (meQuery.isError && !me) {
    body = (
      <Alert tone="danger">
        <Inline gap="space-8" align="baseline" wrap>
          <Text>{settingsCopy.loadFailed.workspace}</Text>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void meQuery.refetch()}
          >
            {settingsCopy.retry}
          </Button>
        </Inline>
      </Alert>
    )
  } else if (!selectedWs) {
    body = (
      <Text tone="text-mute" as="p">
        {settingsCopy.page.notFound}{' '}
        <Link href={ROUTES.home}>{settingsCopy.page.goHome}</Link>
      </Text>
    )
  } else if (deletionPending) {
    body = (
      <Stack gap="space-12" align="start" role="status">
        <Text>{settingsCopy.workspaceDelete.pending}</Text>
        <Text variant="caption" tone="text-mute">
          {settingsCopy.workspaceDelete.pendingHelp}
        </Text>
        {isOwner && (
          <Button
            variant="outline"
            onClick={() => void runDelete(workspaceId, true)}
            loading={deleteState === 'checking'}
          >
            {settingsCopy.workspaceDelete.retryNow}
          </Button>
        )}
      </Stack>
    )
  } else {
    body = (
      <Stack gap="space-32">
        {/* Sub-tab bar */}
        <TabBar
          label={settingsCopy.page.sections}
          tabs={[
            { id: 'members', label: settingsCopy.page.tabs.members },
            { id: 'agents', label: settingsCopy.page.tabs.agents },
            { id: 'keys', label: settingsCopy.page.tabs.keys },
            { id: 'usage', label: settingsCopy.page.tabs.usage },
            { id: 'providers', label: settingsCopy.page.tabs.providers },
          ]}
          active={subTab}
          onChange={(id) => setSubTab(id as WorkspaceSubTab)}
        >
          <TabPanel id="members">
            <MembersTab workspace={selectedWs} />
          </TabPanel>
          <TabPanel id="agents">
            <AgentsTab key={selectedWs.workspace_id} workspace={selectedWs} />
          </TabPanel>
          <TabPanel id="keys">
            <KeysTab wsId={workspaceId} />
          </TabPanel>
          <TabPanel id="usage">
            <UsagePanel wsId={workspaceId} canManage={!!canManage} />
          </TabPanel>
          <TabPanel id="providers">
            <ProvidersTab
              wsId={workspaceId}
              canManage={!!canManage}
              canCreate={!!canManage && workspaceActive}
              creating={creatingProvider}
              onCreatingChange={setCreatingProvider}
            />
          </TabPanel>
        </TabBar>

        {isOwner && workspaceActive && (
          <section>
            <SectionHeader rule trailingSpacing="section">
              {settingsCopy.page.dangerZone}
            </SectionHeader>
            <Button variant="outline" onClick={() => setConfirmingDelete(true)}>
              {settingsCopy.workspaceDelete.action}
            </Button>
          </section>
        )}
      </Stack>
    )
  }

  return (
    <WsPageShell
      crumbs={[
        {
          label: selectedWs?.name ?? settingsCopy.page.crumbFallback,
          to: ROUTES.home,
        },
        { label: settingsCopy.page.title },
      ]}
      title={settingsCopy.page.title}
      meta={
        selectedWs
          ? selectedWs.role
            ? settingsCopy.page.roleLine(selectedWs.name, selectedWs.role)
            : selectedWs.name
          : undefined
      }
      actions={actions}
    >
      {body}

      <ConfirmDialog
        open={confirmingDelete}
        title={settingsCopy.workspaceDelete.title}
        tone="danger"
        confirmText={selectedWs?.name}
        description={settingsCopy.workspaceDelete.description}
        confirmLabel={settingsCopy.workspaceDelete.confirm}
        loading={deleteState === 'checking'}
        onConfirm={() => {
          if (workspaceId) void runDelete(workspaceId)
        }}
        onClose={() => setConfirmingDelete(false)}
      />
    </WsPageShell>
  )
}
